import { Box, Stack, Typography, useMediaQuery } from '@mui/material'
import { FunctionComponent } from 'react'
import gaiaTheme from '../../../root-components/theme';
import { IColorPalatte, IGaiaReportReductionOffsetDetails } from '../shared-services/public-reports.model';
import { NumericFormat } from 'react-number-format';
import { usePdfExportMode } from '../../../hooks/use-pdf-export-mode';
import { getGaiaReportBodyTextSx, getGaiaReportSectionShellSx } from './gaia-report-web-page-pdf-styles';
import { useReportContentOverride } from '../shared-services/gaia-report-content';

export const GaiaReportWebPageNetEmissions: FunctionComponent<{
  companyName: string,
  reductionDetails: IGaiaReportReductionOffsetDetails,
  colorPalatte: IColorPalatte
}> = ({ companyName, reductionDetails, colorPalatte }) => {

  const isMobileModeMd = useMediaQuery(gaiaTheme.breakpoints.down('md'));
  const isMobileModeSm = useMediaQuery(gaiaTheme.breakpoints.down('sm'));
  const pdfExport = usePdfExportMode();

  const heading = useReportContentOverride('netEmissions.heading')
  const body = useReportContentOverride('netEmissions.body')

  return (
    <Stack sx={{
      ...getGaiaReportSectionShellSx({ backgroundColor: 'white', pdfExport }),
      ...(pdfExport ? {} : {
        mt: -5,
        position: 'relative',
        zIndex: 2,
      }),
    }}>
      <Typography variant='h3' sx={{ color: colorPalatte.main, mb: 2 }}>{heading ?? 'Net Emissions'}</Typography>
      <Typography sx={getGaiaReportBodyTextSx({ color: 'text.secondary', isMobileModeMd, isMobileModeSm, pdfExport })}>{body ?? `Net emissions represent ${companyName}’s total emissions after considering both the direct reductions achieved within its operations and the additional reductions secured through offset projects. This figure provides a more complete picture of the company’s overall climate impact by balancing actual emissions with the measures taken to mitigate them.`}</Typography>
      <Stack display='flex' flexDirection='row' gap={0} justifyContent='center' alignItems='center' sx={{ mb: 1, ml: 1, mr: 1 }}>
        <Stack display='flex' flexDirection='column' gap={1} justifyContent='center' alignItems='center' sx={{ mb: 1 }}>
          <Box sx={{ backgroundColor: colorPalatte.veryDark, borderRadius: '20px', p: 2, width: isMobileModeMd ? '125px' : '175px' }}>
            <Typography sx={{ color: 'white', fontSize: isMobileModeMd ? '0.85rem' : '1.25rem', textAlign: 'center' }}><NumericFormat value={reductionDetails.netEmission.total} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
          </Box>
          <Box sx={{ backgroundColor: colorPalatte.veryDark, borderRadius: '20px', p: 2, width: isMobileModeMd ? '125px' : '175px' }}>
            <Typography sx={{ color: 'white', fontSize: isMobileModeMd ? '0.85rem' : '1.25rem', textAlign: 'center' }}>Total Emissions</Typography>
          </Box>
        </Stack>
        <Stack display='flex' flexDirection='column' gap={1} justifyContent='center' alignItems='center' sx={{ mb: 1 }}>
          <Box sx={{ backgroundColor: 'white', borderRadius: '20px', p: 2, width: isMobileModeMd ? '50px' : '70px' }}>
            <Typography sx={{ color: 'text.primary', fontWeight: 600, fontSize: isMobileModeMd ? '0.85rem' : '1.25rem', textAlign: 'center' }}>-</Typography>
          </Box>
          <Box sx={{ backgroundColor: 'white', borderRadius: '20px', p: 2, width: isMobileModeMd ? '50px' : '70px' }}>
            <Typography sx={{ color: 'text.primary', fontWeight: 600, fontSize: isMobileModeMd ? '0.85rem' : '1.25rem', textAlign: 'center' }}>-</Typography>
          </Box>
        </Stack>
        <Stack display='flex' flexDirection='column' gap={1} justifyContent='center' alignItems='center' sx={{ mb: 1 }}>
          <Box sx={{ backgroundColor: colorPalatte.dark, borderRadius: '20px', p: 2, width: isMobileModeMd ? '125px' : '175px' }}>
            <Typography sx={{ color: 'white', fontSize: isMobileModeMd ? '0.85rem' : '1.25rem', textAlign: 'center' }}><NumericFormat value={reductionDetails.total} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
          </Box>
          <Box sx={{ backgroundColor: colorPalatte.dark, borderRadius: '20px', p: 2, width: isMobileModeMd ? '125px' : '175px' }}>
            <Typography sx={{ color: 'white', fontSize: isMobileModeMd ? '0.85rem' : '1.25rem', textAlign: 'center' }}>Offsets</Typography>
          </Box>
        </Stack>
        <Stack display='flex' flexDirection='column' gap={1} justifyContent='center' alignItems='center' sx={{ mb: 1 }}>
          <Box sx={{ backgroundColor: 'white', borderRadius: '20px', p: 2, width: isMobileModeMd ? '50px' : '70px' }}>
            <Typography sx={{ color: 'text.primary', fontWeight: 600, fontSize: isMobileModeMd ? '0.85rem' : '1.25rem', textAlign: 'center' }}>=</Typography>
          </Box>
          <Box sx={{ backgroundColor: 'white', borderRadius: '20px', p: 2, width: isMobileModeMd ? '50px' : '70px' }}>
            <Typography sx={{ color: 'text.primary', fontWeight: 600, fontSize: isMobileModeMd ? '0.85rem' : '1.25rem', textAlign: 'center' }}>=</Typography>
          </Box>
        </Stack>
        <Stack display='flex' flexDirection='column' gap={1} justifyContent='center' alignItems='center' sx={{ mb: 1 }}>
          <Box sx={{ backgroundColor: colorPalatte.main, borderRadius: '20px', p: 2, width: isMobileModeMd ? '125px' : '175px' }}>
            <Typography sx={{ color: 'white', fontSize: isMobileModeMd ? '0.85rem' : '1.25rem', textAlign: 'center' }}><NumericFormat value={reductionDetails.netEmission.net} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
          </Box>
          <Box sx={{ backgroundColor: colorPalatte.main, borderRadius: '20px', p: 2, width: isMobileModeMd ? '125px' : '175px' }}>
            <Typography sx={{ color: 'white', fontSize: isMobileModeMd ? '0.85rem' : '1.25rem', textAlign: 'center' }}>Net Emissions</Typography>
          </Box>
        </Stack>
      </Stack>
    </Stack >
  )
}